import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import {
  formatPeriode,
  getAggregatesForPeriod,
  getRowPeriode,
} from "./dashboardPeriodUtils";
import { useI18n } from "../i18n/I18nContext";
import { PLIS_TO_MM_LABEL, PLIS_RECONNUS } from "../utils/empilementPopulations";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = {
  mor: "#639922",
  morDark: "#3f6310",
  moy: "#EF9F27",
  bar: "#4a5568",
  barEdge: "#1e3a50",
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
};

const POPULATION_KEYS = ["global", ...PLIS_RECONNUS.map(String)];
const POPULATION_LABELS = {
  global: "GLOBAL",
  ...Object.fromEntries(
    PLIS_RECONNUS.map((n) => [String(n), PLIS_TO_MM_LABEL[n]])
  ),
};

function ChartTooltip({ active, payload, label, showFmk = true }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload
        .filter((p) => showFmk || p.dataKey !== "fmk")
        .map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontSize: 12 }}>
          {p.name}: {p.value ?? "N/A"}
          {p.dataKey === "fmk" || p.dataKey === "moyenne" ? " MPa" : ""}
        </div>
      ))}
    </div>
  );
}

function BreakdownDetail({ row, periodMode }) {
  const bd = row.breakdown;
  const periodLabel = formatPeriode(getRowPeriode(row), periodMode);
  if (!bd) return null;

  if (bd.fmk == null) {
    return (
      <div style={S.detailBox}>
        <div style={S.detailTitle}>fm,k (EN 14358, loi log-normale) — {periodLabel}</div>
        <div style={{ color: COLORS.gray, fontSize: 12 }}>
          n = {bd.n} &lt; 2 → fm,k = N/A
        </div>
      </div>
    );
  }

  return (
    <div style={S.detailBox}>
      <div style={S.detailTitle}>fm,k (EN 14358, loi log-normale) — {periodLabel}</div>
      <div style={S.detailLine}>x_j retenus (n={bd.n}) = [{bd.xs.map((v) => v.toFixed(2)).join(", ")}] MPa</div>
      <div style={S.detailLine}>y_bar = {bd.y_bar}</div>
      <div style={S.detailLine}>s_y (ddof=1) = {bd.sy_raw}</div>
      <div style={S.detailLine}>
        plancher = {bd.floor} → s_y retenu = {bd.sy}
      </div>
      <div style={S.detailLine}>k_s({bd.n}) = {bd.ks}</div>
      <div style={{ ...S.detailLine, color: COLORS.mor, fontWeight: 700 }}>
        fm,k = exp({bd.y_bar} − {bd.ks} × {bd.sy}) = {bd.fmk} MPa
      </div>
      <div style={S.detailLine}>Moyenne des moyennes = {row.moyenne ?? "N/A"} MPa</div>
    </div>
  );
}

export default function DashboardMorSection({ atTop = false, externalData = null }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(!externalData);
  const [error, setError] = useState("");
  const [data, setData] = useState(externalData);

  useEffect(() => {
    if (externalData) {
      setData(externalData);
      setLoading(false);
      setError("");
      return;
    }
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/dashboard/mor");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || "Erreur API");
        setData(json);
      } catch (e) {
        setError(e.message || "Erreur chargement MOR");
      } finally {
        setLoading(false);
      }
    })();
  }, [externalData]);

  const [population, setPopulation] = useState("global");
  const [periodMode, setPeriodMode] = useState("month");
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  const { rows, weekUnavailable } = useMemo(() => {
    const { rows: r, weekUnavailable: wu } = getAggregatesForPeriod(data, population, periodMode);
    return { rows: r, weekUnavailable: wu };
  }, [data, population, periodMode]);

  const periodLabelCol = periodMode === "week" ? "Semaine" : "Mois";
  const chartTitle = periodMode === "week" ? "Graphique hebdomadaire" : "Graphique mensuel";
  const tableTitle = periodMode === "week" ? "Récapitulatif hebdomadaire" : "Récapitulatif mensuel";
  const showFmk = periodMode === "month";

  const tableHeaders = showFmk
    ? [periodLabelCol, "fm,k (MPa)", "Moyenne (MPa)", "nb Stacks Flexion", "nb Stacks Produits", ""]
    : [periodLabelCol, "Moyenne (MPa)", "nb Stacks Flexion", "nb Stacks Produits", ""];

  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        periode: formatPeriode(getRowPeriode(r), periodMode),
        n_produits: r.n_produits,
        fmk: r.fmk,
        moyenne: r.moyenne,
      })),
    [rows, periodMode]
  );

  const populationLabel = data?.populations?.[population] || population;

  if (loading) {
    return (
      <div style={atTop ? S.sectionTop : S.section}>
        <div style={{ color: COLORS.yellow, padding: 24 }}>Chargement MOR…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={atTop ? S.sectionTop : S.section}>
        <div style={{ color: COLORS.red, padding: 24 }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={atTop ? S.sectionTop : S.section}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Suivi MOR{showFmk ? " — fm,k" : ""}</div>
          <div style={S.subtitle}>
            {showFmk
              ? `Valeur caractéristique EN 14358 (loi log-normale) · éprouvettes < ${data?.params?.mor_min_mpa} MPa exclues`
              : "Suivi hebdomadaire · moyenne des moyennes uniquement (fm,k réservé au mensuel)"}
          </div>
        </div>
      </div>

      <div style={S.tabRow}>
        <button
          type="button"
          style={{ ...S.tab, ...(periodMode === "month" ? S.tabActive : {}) }}
          onClick={() => { setPeriodMode("month"); setExpandedPeriod(null); }}
        >
          Par mois
        </button>
        <button
          type="button"
          style={{ ...S.tab, ...(periodMode === "week" ? S.tabActive : {}) }}
          onClick={() => { setPeriodMode("week"); setExpandedPeriod(null); }}
        >
          Par semaine
        </button>
      </div>

      <div style={S.tabRow}>
        {POPULATION_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            style={{ ...S.tab, ...(population === key ? S.tabActive : {}) }}
            onClick={() => {
              setPopulation(key);
              setExpandedPeriod(null);
            }}
          >
            {POPULATION_LABELS[key]}
          </button>
        ))}
      </div>

      <div style={{ color: COLORS.gray, fontSize: 12, marginBottom: 16 }}>{populationLabel}</div>

      {weekUnavailable && (
        <div style={{ ...S.card, borderColor: COLORS.red, marginBottom: 16 }}>
          <div style={{ color: COLORS.red, fontSize: 13 }}>
            Données hebdomadaires indisponibles. Redémarrez le backend Flask pour charger la dernière version.
          </div>
        </div>
      )}

      {chartData.length === 0 ? (
        <div style={S.card}>
          <div style={{ color: COLORS.gray }}>Aucune donnée pour cette population.</div>
        </div>
      ) : (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>{chartTitle}</div>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="periode" tick={{ fill: COLORS.gray, fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: COLORS.gray, fontSize: 11 }}
                  allowDecimals={false}
                  label={{ value: "Stacks produits", angle: -90, position: "insideLeft", fill: COLORS.gray, fontSize: 11 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: COLORS.morDark, fontSize: 11 }}
                  label={{ value: "MOR (MPa)", angle: 90, position: "insideRight", fill: COLORS.morDark, fontSize: 11 }}
                />
                <Tooltip content={<ChartTooltip showFmk={showFmk} />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="n_produits"
                  name="Stacks produits"
                  fill={COLORS.bar}
                  stroke={COLORS.barEdge}
                  radius={[4, 4, 0, 0]}
                />
                {showFmk && (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="fmk"
                    name="fm,k (valeur carac. MOR)"
                    stroke={COLORS.mor}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: COLORS.mor }}
                    connectNulls={false}
                  />
                )}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="moyenne"
                  name="Moyenne des moyennes"
                  stroke={COLORS.moy}
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: COLORS.moy }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>{tableTitle}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {tableHeaders.map((h) => (
                      <th key={h || "action"} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const rowPeriode = getRowPeriode(row);
                    return (
                    <React.Fragment key={rowPeriode}>
                      <tr style={S.tr}>
                        <td style={S.td}>{formatPeriode(rowPeriode, periodMode)}</td>
                        {showFmk && (
                          <td style={{ ...S.td, color: COLORS.mor, fontWeight: 700 }}>
                            {row.fmk != null ? row.fmk : "N/A (n<2)"}
                          </td>
                        )}
                        <td style={{ ...S.td, color: COLORS.moy }}>
                          {row.moyenne ?? "N/A"}
                        </td>
                        <td style={S.td}>{row.n_panels}</td>
                        <td style={S.td}>{row.n_produits}</td>
                        <td style={S.td}>
                          <button
                            type="button"
                            style={S.detailBtn}
                            onClick={() =>
                              setExpandedPeriod((p) => (p === rowPeriode ? null : rowPeriode))
                            }
                          >
                            {expandedPeriod === rowPeriode ? "Masquer" : "Détail"}
                          </button>
                        </td>
                      </tr>
                      {expandedPeriod === rowPeriode && (
                        <tr>
                          <td colSpan={tableHeaders.length} style={{ padding: "12px 14px", background: "rgba(0,0,0,0.2)" }}>
                            {showFmk && <BreakdownDetail row={row} periodMode={periodMode} />}
                            <div style={{ marginTop: showFmk ? 12 : 0 }}>
                              <div style={S.detailTitle}>
                                Stacks de la période ({row.stacks?.length || 0})
                              </div>
                              {(row.stacks || []).map((d) => {
                                const excl = d.n_rows - d.n_valid;
                                return (
                                  <div key={d.stack} style={S.stackLine}>
                                    <span style={{ color: COLORS.yellow, fontWeight: 700 }}>{d.stack}</span>
                                    {" · "}
                                    {d.n_rows} éprouvette(s) → [{d.vals?.length ? d.vals.map((v) => v.toFixed(3)).join(", ") : "aucune"}] MPa
                                    {" → "}
                                    x_j = {d.x_j != null ? d.x_j.toFixed(2) : "N/A"}
                                    {excl > 0 && (
                                      <span style={{ color: COLORS.red }}>
                                        {" "}({excl} &lt; {data?.params?.mor_min_mpa} MPa exclue(s))
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  sectionTop: {
    marginTop: 24,
    paddingTop: 24,
    marginBottom: 28,
    borderTop: "1px solid #1e3a50",
  },
  section: {
    marginTop: 32,
    paddingTop: 32,
    borderTop: "1px solid #1e3a50",
  },
  header: { marginBottom: 20 },
  title: {
    fontSize: 20,
    fontWeight: 900,
    color: "#fff",
    letterSpacing: "0.04em",
  },
  subtitle: {
    fontSize: 12,
    color: COLORS.gray,
    marginTop: 4,
  },
  tabRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  tab: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: COLORS.gray,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabActive: {
    borderColor: COLORS.yellow,
    color: COLORS.yellow,
    background: "rgba(230,255,62,0.08)",
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.gray,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    color: COLORS.gray,
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 10,
    letterSpacing: "0.06em",
    borderBottom: "1px solid #1e3a50",
  },
  tr: { borderBottom: "1px solid #1e3a50" },
  td: { padding: "10px 14px", color: "#e2e8f0" },
  detailBtn: {
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.06)",
    color: COLORS.yellow,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  detailBox: { marginBottom: 8 },
  detailTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.yellow,
    marginBottom: 8,
  },
  detailLine: {
    fontSize: 12,
    color: "#cbd5e1",
    marginBottom: 4,
    fontFamily: "monospace",
  },
  stackLine: {
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 4,
    fontFamily: "monospace",
  },
  tooltip: {
    background: "#071e2f",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 14px",
  },
};
